import { cn } from '@/lib/utils'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { staggerContainer } from '@/v2/constants/motion'
import { CardType } from '@/v2/types/cards'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import React from 'react'

interface OurProcessSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  description?: string
  image: { url: string; alt: string }
  ctaButton?: { href: string; text: string }
  imgClassName?: string
  btnClassName?: string
  sectionTitleClassName?: string
  isBgDark?: boolean
  articles: CardType[]
}

const OurProcessSection: React.FC<OurProcessSectionProps> = props => {
  const {
    title,
    description,
    image,
    ctaButton,
    className,
    imgClassName,
    btnClassName,
    sectionTitleClassName,
    isBgDark = false,
    articles,
    ...rest
  } = props
  const cardBackground = isBgDark ? 'bg-[#fff]' : 'bg-[#F9F8F8]'

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        variants={staggerContainer()}
        initial='hidden'
        whileInView='show'
        viewport={{ once: true, amount: 0.25 }}
        className={cn('landing-pages-wr section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '', className)}
      >
        <SectionWrapper {...rest}>
          <SectionHead title={title} description={description} className={sectionTitleClassName} />

          <div className='flex items-center lg-mn:flex-row flex-col xl-min:!gap-20 lg-mn:gap-5 gap-10'>
            <m.div className='my-10 lg-mn:my-0'>
              <ResponsiveImage
                src={image.url}
                alt={image.alt}
                className={cn(
                  'w-[257px] h-[144px] md-mn:w-[296px] md-mn:h-[166px] xl-min:!h-[423px] xl-min:!w-[618px] object-contain',
                  imgClassName
                )}
                imgClassName={imgClassName}
              />
            </m.div>
            <m.div className='flex flex-col gap-4'>
              {articles?.map(article => {
                return (
                  <article
                    key={article.title}
                    className={cn(
                      'flex items-center gap-5 lg-mn:p-5 p-3 rounded-md border-2 border-[#E5E5E5] transition-all hover:border-[#0A9CFF]',
                      cardBackground
                    )}
                  >
                    {article.image && (
                      <div>
                        <ResponsiveImage
                          src={article.image.src}
                          alt={article.image.alt}
                          className={'w-[80px] h-[80px]'}
                        />
                      </div>
                    )}

                    <div className='[&>h3]:mb-[11px] xl-min:[&>h3]:text-xl w-full [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:text-paragraph [&>h3]:leading-8'>
                      <h3>{article.title}</h3>
                      <p className='text-paragraph leading-6'>{article.description}</p>
                    </div>
                  </article>
                )
              })}
            </m.div>
          </div>

          {/* CTA Button */}
          {ctaButton && (
            <LinkWithTransition href={ctaButton.href} className='mx-auto flex justify-center w-fit'>
              <Button
                variant='call_to_action'
                className={cn(
                  'transition-all animate-bounce hover:animated-paused mt-12 lg-mn:w-[208px] xl-min:h-[50px] w-[191px] h-[44px]',
                  btnClassName
                )}
              >
                {ctaButton.text}
              </Button>
            </LinkWithTransition>
          )}
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default OurProcessSection
